import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CloudOff as CloudOffIcon,
  CloudSync as CloudSyncIcon
} from '@mui/icons-material';
import oneDriveService, { checkActaCompleteness, uploadActaToOneDrive } from '../services/oneDriveService';
import pdfConfig from '../utils/pdfConfig';
import ActaPDF from './ActaPDF';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';

const OneDriveManager = ({ actas, onActaUpdate }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingActa, setUploadingActa] = useState(null);
  const [oneDriveFiles, setOneDriveFiles] = useState([]);
  const [autoUploadEnabled, setAutoUploadEnabled] = useState(false);
  const [selectedActa, setSelectedActa] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actaStatuses, setActaStatuses] = useState({});

  useEffect(() => {
    initializeOneDrive();
    checkActasStatuses();
  }, [actas]);

  const initializeOneDrive = async () => {
    try {
      setLoading(true);
      await oneDriveService.initialize();
      setIsAuthenticated(oneDriveService.isAuthenticated);
    } catch (error) {
      console.error('Error inicializando OneDrive:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setLoading(true);
      await oneDriveService.authenticate();
      setIsAuthenticated(true);
      showSnackbar('Autenticación exitosa con OneDrive', 'success');
      await loadOneDriveFiles();
    } catch (error) {
      console.error('Error en autenticación:', error);
      showSnackbar('Error en la autenticación con OneDrive', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOneDriveFiles = async () => {
    try {
      const files = await oneDriveService.getActasList();
      setOneDriveFiles(files);
    } catch (error) {
      console.error('Error cargando archivos de OneDrive:', error);
    }
  };

  const checkActasStatuses = () => {
    const statuses = {};
    actas.forEach(acta => {
      statuses[acta.id] = checkActaCompleteness(acta);
    });
    setActaStatuses(statuses);
  };

  const handleUploadActa = async (acta, forceReplace = false) => {
    try {
      setUploadingActa(acta.id);
      
      // Verificar que el acta esté completa
      const status = checkActaCompleteness(acta);
      if (!status.complete) {
        showSnackbar(`No se puede subir: ${status.reason}`, 'warning');
        return;
      }

      // Verificar si ya existe en OneDrive y no es un reemplazo forzado
      if (acta.uploadedToOneDrive && !forceReplace) {
        const shouldReplace = window.confirm(
          `El acta #${acta.numeroActa} ya está subida a OneDrive. ¿Deseas reemplazarla?`
        );
        if (!shouldReplace) {
          setUploadingActa(null);
          return;
        }
      }

      let pdfBlob;
      
      // Para actas externas, usar el PDF existente
      if (acta.isExternal) {
        try {
          if (acta.pdfUrl) {
            // Si hay pdfUrl, descargar desde la URL
            const response = await fetch(acta.pdfUrl);
            pdfBlob = await response.blob();
          } else if (acta.pdfBase64) {
            // Si hay pdfBase64, convertir a blob
            const base64Data = acta.pdfBase64.split(',')[1]; // Remover el prefijo data:application/pdf;base64,
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            pdfBlob = new Blob([bytes], { type: 'application/pdf' });
          } else {
            throw new Error('No se encontró PDF en el acta externa');
          }
        } catch (error) {
          console.error('Error procesando PDF externo:', error);
          showSnackbar('Error accediendo al PDF del acta externa', 'error');
          return;
        }
      } else {
        // Generar PDF para actas normales
        pdfBlob = await pdfConfig.generatePdfBlob(<ActaPDF data={acta} />);
      }

      // Subir a OneDrive
      const result = await uploadActaToOneDrive(acta, pdfBlob);
      
      if (result.success) {
        const message = acta.uploadedToOneDrive 
          ? `Acta #${acta.numeroActa} reemplazada exitosamente en OneDrive`
          : `Acta #${acta.numeroActa} subida exitosamente a OneDrive`;
        
        showSnackbar(message, 'success');
        
        // Actualizar el acta con la información de OneDrive
        const updatedActa = {
          ...acta,
          oneDriveFileId: result.fileId,
          oneDriveUrl: result.webUrl,
          uploadedToOneDrive: true,
          uploadDate: new Date(),
          lastModified: new Date()
        };
        
        // Actualizar en Firebase
        try {
          const actaRef = doc(db, 'actas', acta.id);
          await updateDoc(actaRef, {
            oneDriveFileId: result.fileId,
            oneDriveUrl: result.webUrl,
            uploadedToOneDrive: true,
            uploadDate: new Date(),
            lastModified: new Date()
          });
        } catch (firebaseError) {
          console.error('❌ Error actualizando Firebase:', firebaseError);
          showSnackbar('Archivo subido pero error actualizando estado en base de datos', 'warning');
        }
        
        // Actualizar estado local
        if (onActaUpdate) {
          onActaUpdate(updatedActa);
        }
        
        await loadOneDriveFiles();
      }
    } catch (error) {
      console.error('Error subiendo acta:', error);
      showSnackbar(`Error subiendo acta: ${error.message}`, 'error');
    } finally {
      setUploadingActa(null);
    }
  };

  const runDiagnostics = async () => {
    try {
      setLoading(true);
      
      const diagnosticResults = await oneDriveService.runDiagnostics();
      setDiagnostics(diagnosticResults);
      setShowDiagnostics(true);
      
      if (diagnosticResults.errors.length === 0) {
        showSnackbar('Diagnósticos completados: Todo funciona correctamente', 'success');
      } else {
        showSnackbar(`Diagnósticos completados: ${diagnosticResults.errors.length} problemas encontrados`, 'warning');
      }
    } catch (error) {
      console.error('Error ejecutando diagnósticos:', error);
      showSnackbar(`Error en diagnósticos: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    const completedActas = actas.filter(acta => 
      checkActaCompleteness(acta).complete && !acta.uploadedToOneDrive
    );

    if (completedActas.length === 0) {
      showSnackbar('No hay actas completas para subir', 'info');
      return;
    }

    setShowUploadDialog(true);
    setSelectedActa(completedActas);
  };

  const confirmBulkUpload = async () => {
    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;
      
      for (const acta of selectedActa) {
        try {
          await handleUploadActa(acta);
          successCount++;
        } catch (error) {
          console.error(`Error subiendo acta ${acta.numeroActa}:`, error);
          errorCount++;
        }
      }
      
      if (errorCount === 0) {
        showSnackbar(`${successCount} actas subidas exitosamente`, 'success');
      } else {
        showSnackbar(`${successCount} actas subidas, ${errorCount} con errores`, 'warning');
      }
    } catch (error) {
      showSnackbar('Error en la subida masiva', 'error');
    } finally {
      setLoading(false);
      setShowUploadDialog(false);
      setSelectedActa(null);
    }
  };

  const getActaStatusChip = (acta) => {
    const status = actaStatuses[acta.id];
    if (!status) return null;

    if (acta.uploadedToOneDrive) {
      const uploadDate = acta.uploadDate ? new Date(acta.uploadDate) : null;
      const lastModified = acta.lastModified ? new Date(acta.lastModified) : null;
      const isUpdated = lastModified && uploadDate && lastModified > uploadDate;
      
      return (
        <Chip
          icon={<CloudUploadIcon />}
          label={isUpdated ? "Actualizado en OneDrive" : "En OneDrive"}
          color={isUpdated ? "info" : "success"}
          size="small"
        />
      );
    }

    if (status.complete) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Lista para subir"
          color="primary"
          size="small"
        />
      );
    }

    return (
      <Chip
        icon={<WarningIcon />}
        label="Incompleta"
        color="warning"
        size="small"
      />
    );
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading && !isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Inicializando OneDrive...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" color="primary">
            Gestión de OneDrive
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            {isAuthenticated ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Conectado"
                color="success"
              />
            ) : (
              <Chip
                icon={<CloudOffIcon />}
                label="Desconectado"
                color="error"
              />
            )}
            <IconButton onClick={loadOneDriveFiles} disabled={!isAuthenticated}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {!isAuthenticated ? (
          <Box textAlign="center" py={4}>
            <CloudOffIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Conectar con OneDrive
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Conecta tu cuenta de Microsoft para subir automáticamente las actas completas
            </Typography>
            <Button
              variant="contained"
              onClick={handleAuthenticate}
              startIcon={<CloudUploadIcon />}
              disabled={loading}
            >
              {loading ? 'Conectando...' : 'Conectar OneDrive'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoUploadEnabled}
                    onChange={(e) => setAutoUploadEnabled(e.target.checked)}
                  />
                }
                label="Subida automática de actas completas"
              />
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={runDiagnostics}
                  startIcon={<SettingsIcon />}
                  disabled={loading}
                >
                  Diagnóstico
                </Button>
                <Button
                  variant="contained"
                  onClick={handleBulkUpload}
                  startIcon={<CloudUploadIcon />}
                  disabled={loading}
                >
                  Subir Actas Completas
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Estado de las Actas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {actas.filter(a => a.uploadedToOneDrive).length} de {actas.length} actas en OneDrive
              </Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Acta #</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Firmas</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {actas.map((acta) => {
                    const status = actaStatuses[acta.id];
                    const firmasCount = acta.asistentes?.filter(a => a.firma).length || 0;
                    const totalAsistentes = acta.asistentes?.length || 0;

                    return (
                      <TableRow key={acta.id}>
                        <TableCell>{acta.numeroActa}</TableCell>
                        <TableCell>
                          {acta.fecha instanceof Date 
                            ? acta.fecha.toLocaleDateString('es-ES')
                            : new Date(acta.fecha).toLocaleDateString('es-ES')
                          }
                        </TableCell>
                        <TableCell>
                          {getActaStatusChip(acta)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {firmasCount}/{totalAsistentes}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {acta.uploadedToOneDrive ? (
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => window.open(acta.oneDriveUrl, '_blank')}
                              >
                                Ver en OneDrive
                              </Button>
                              <Button
                                 size="small"
                                 variant="outlined"
                                 startIcon={<CloudSyncIcon />}
                                 onClick={() => handleUploadActa(acta, true)}
                                 disabled={uploadingActa === acta.id}
                                 color="warning"
                               >
                                {uploadingActa === acta.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  'Reemplazar'
                                )}
                              </Button>
                            </Box>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CloudUploadIcon />}
                              onClick={() => handleUploadActa(acta)}
                              disabled={!status?.complete || uploadingActa === acta.id}
                            >
                              {uploadingActa === acta.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                'Subir'
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Dialog de confirmación para subida masiva */}
      <Dialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)}>
        <DialogTitle>Confirmar Subida Masiva</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Estás seguro de que quieres subir las siguientes actas a OneDrive?
          </Typography>
          <List>
            {selectedActa?.map((acta) => (
              <ListItem key={acta.id}>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={`Acta #${acta.numeroActa}`}
                  secondary={`Fecha: ${acta.fecha instanceof Date 
                    ? acta.fecha.toLocaleDateString('es-ES')
                    : new Date(acta.fecha).toLocaleDateString('es-ES')
                  }`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>Cancelar</Button>
          <Button
            onClick={confirmBulkUpload}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          >
            {loading ? 'Subiendo...' : 'Confirmar Subida'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de diagnósticos */}
      <Dialog 
        open={showDiagnostics} 
        onClose={() => setShowDiagnostics(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Diagnóstico de OneDrive</DialogTitle>
        <DialogContent>
          {diagnostics && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Resultados del Diagnóstico
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    {diagnostics.authentication ? 
                      <CheckCircleIcon color="success" /> : 
                      <ErrorIcon color="error" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Autenticación" 
                    secondary={diagnostics.authentication ? "Exitosa" : "Falló"}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    {diagnostics.driveAccess ? 
                      <CheckCircleIcon color="success" /> : 
                      <ErrorIcon color="error" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Acceso a OneDrive" 
                    secondary={diagnostics.driveAccess ? "Conectado" : "Sin acceso"}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    {diagnostics.permissions ? 
                      <CheckCircleIcon color="success" /> : 
                      <ErrorIcon color="error" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Permisos de Lectura/Escritura" 
                    secondary={diagnostics.permissions ? "Correctos" : "Insuficientes"}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    {diagnostics.folderCreation ? 
                      <CheckCircleIcon color="success" /> : 
                      <ErrorIcon color="error" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Creación de Carpetas" 
                    secondary={diagnostics.folderCreation ? "Funcional" : "Con problemas"}
                  />
                </ListItem>
              </List>
              
              {diagnostics.errors.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Errores Encontrados:
                  </Typography>
                  {diagnostics.errors.map((error, index) => (
                    <Alert key={index} severity="error" sx={{ mb: 1 }}>
                      {error}
                    </Alert>
                  ))}
                </Box>
              )}
              
              {diagnostics.errors.length === 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  ✅ Todos los diagnósticos pasaron correctamente. OneDrive está funcionando bien.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiagnostics(false)}>Cerrar</Button>
          <Button onClick={runDiagnostics} variant="outlined" disabled={loading}>
            Ejecutar Nuevamente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OneDriveManager;
